<template>
  <a-modal
    :visible="visible"
    title="管理配方组合包"
    :width="800"
    :destroyOnClose="true"
    :maskClosable="false"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="formula-bag-container">
      <!-- 功能设置区域 -->
      <div class="function-settings">
        <h3 class="section-title">功能设置</h3>
        <a-alert
          message="功能开启后,当前配方中原料和指标的编辑将在配方优化时,同步更新到组合包中的其他配方;并且在配方优化和生成产品时,组合包中的配方将同步进行;组合明细中配方数量≤5个配方。"
          type="info"
          show-icon
          style="margin-bottom: 16px;"
        />
        <div class="toggle-section">
          <span class="toggle-label">是否开启功能:</span>
          <a-switch 
            v-model="functionEnabled" 
            checked-children="开" 
            un-checked-children="关"
            @change="handleFunctionToggle"
          />
        </div>
      </div>

      <!-- 组合明细区域 -->
      <div class="combination-details">
        <div class="section-header">
          <h3 class="section-title">组合明细</h3>
          <a-button type="primary" @click="handleImportFormula" :disabled="!functionEnabled">
            导入配方
          </a-button>
        </div>
        
        <a-table
          :columns="columns"
          :dataSource="formulaList"
          :pagination="false"
          :loading="tableLoading"
          :rowKey="getRowKeyFn"
          size="small"
        >
          <template slot="operation" slot-scope="text, record">
            <a-button 
              type="link" 
              danger 
              size="small"
              @click="handleRemoveFormula(record)"
              :disabled="!functionEnabled"
            >
              移出
            </a-button>
          </template>
        </a-table>
      </div>
    </div>

    <!-- 导入配方弹窗 -->
    <ImportFormula 
      :visible="importFormulaVisible" 
      :currentFormulaId="formulaId"
      :existingCount="formulaList.length"
      @close="toggleImportFormula"
      @success="handleImportSuccess"
    />
  </a-modal>
</template>

<script>
import { post, get } from '@/Utils/axios'
import ImportFormula from './ImportFormula.vue'

export default {
  name: 'FormulaBag',
  components: {
    ImportFormula
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    formulaId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      tableLoading: false,
      functionEnabled: false,
      // 初始化时抑制开关触发更新接口
      suppressToggle: false,
      importFormulaVisible: false,
      formulaList: [],
      columns: [
        {
          title: '配方名称',
          dataIndex: 'formulaName',
          key: 'formulaName',
          width: 150
        },
        {
          title: '配方代码',
          dataIndex: 'formulaCode',
          key: 'formulaCode',
          width: 180
        },
        {
          title: '动物类型',
          dataIndex: 'animalTypeName',
          key: 'animalTypeName',
          width: 120
        },
        {
          title: '分类',
          dataIndex: 'typeClassify',
          key: 'typeClassify',
          width: 120
        },
        {
          title: '操作',
          key: 'operation',
          width: 80,
          scopedSlots: { customRender: 'operation' }
        }
      ]
    }
  },
  watch: {
    visible(val) {
      if (val) {
        // 加载配方详情，初始化功能开关等信息
        this.loadFormulaDetail();
        // 加载组合包列表
        this.loadFormulaBagData();
      }
    }
  },
  methods: {
    getRowKeyFn(record, index) {
      // 确保每一行都有唯一的 key
      return record.id ? String(record.id) : `row-${index}`
    },
    
    handleOk() {
      this.loading = true;
      // 这里可以添加保存逻辑
      setTimeout(() => {
        this.loading = false;
        this.$emit('close');
        this.$emit('refresh'); // 通知父组件刷新
        this.$message.success('保存成功');
      }, 1000);
    },

    async loadFormulaDetail() {
      try {
        const res = await get('/nhf-app/formulaManage/detail', { formulaId: this.formulaId })
        if (res) {
          const data = res.data || res
          // isStartBag 可能为 '1' | '0' | 1 | 0 | true | false
          const isStartBagValue = data.isStartBag
          this.suppressToggle = true
          this.functionEnabled = isStartBagValue === '1' || isStartBagValue === 1 || isStartBagValue === true
          this.$nextTick(() => {
            this.suppressToggle = false
          })
        }
      } catch (e) {
        // 详情失败不阻塞主流程
      }
    },

    handleCancel() {
      this.$emit('close');
      this.$emit('refresh'); // 通知父组件刷新
    },

    async handleFunctionToggle(checked) {
      if (this.suppressToggle) return
      this.functionEnabled = checked;
      try {
        const params = {
          isStartBag: checked ? '1' : '0',
          id: this.formulaId
        }
        const res = await post('/nhf-app/formulaManage/updateFormulaIsStartBag', params)
        if (res) {
          this.$message.success(checked ? '功能已开启' : '功能已关闭')
        }
      } catch (error) {
        this.$message.error('更新失败，请重试')
        // 如果失败，恢复原状态
        this.functionEnabled = !checked
      }
    },

    handleImportFormula() {
      this.importFormulaVisible = true;
    },

    toggleImportFormula() {
      this.importFormulaVisible = !this.importFormulaVisible;
    },

    handleImportSuccess() {
      // 导入成功后刷新列表
      this.loadFormulaBagData();
    },

    handleRemoveFormula(record) {
      this.$confirm({
        title: '确认移出',
        content: `确定要移出配方"${record.formulaName}"吗？`,
        okText: '确定',
        cancelText: '取消',
        onOk: async () => {
          try {
            this.tableLoading = true
            await post('/nhf-app/formulabag/delFormulaBag', { idList: [record.id] })
            this.$message.success('移出成功')
            // 刷新列表，确保与服务端数据一致
            await this.loadFormulaBagData()
          } catch (error) {
            this.$message.error('移出失败，请重试')
          } finally {
            this.tableLoading = false
          }
        }
      });
    },

    async loadFormulaBagData() {
      this.tableLoading = true;
      try {
        const params = {
          formulaId: this.formulaId,
          query: {
            current: 1,
            size: 1000
          }
        }
        const res = await post('/nhf-app/formulabag/pageFormulaBagVO', params)
        
        // 兼容多种可能的数据结构
        if (res) {
          if (res.records) {
            this.formulaList = res.records || []
          } else if (Array.isArray(res)) {
            this.formulaList = res
          } else if (res.data && Array.isArray(res.data)) {
            this.formulaList = res.data
          } else if (res.data && res.data.records) {
            this.formulaList = res.data.records || []
          } else {
            this.formulaList = []
          }
        }
      } catch (error) {
        this.$message.error('加载配方组合包数据失败')
        this.formulaList = []
      } finally {
        this.tableLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.formula-bag-container {
  .function-settings {
    margin-bottom: 24px;
    
    .section-title {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 12px;
      color: #333;
    }
    
    .toggle-section {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .toggle-label {
        font-size: 14px;
        color: #666;
      }
    }
  }
  
  .combination-details {
    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      
      .section-title {
        font-size: 16px;
        font-weight: bold;
        margin: 0;
        color: #333;
      }
    }
  }
}

::v-deep .ant-table-thead > tr > th {
  background-color: #fafafa;
  font-weight: 600;
}

::v-deep .ant-table-tbody > tr > td {
  border-bottom: 1px solid #f0f0f0;
}
</style>
